import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { ChakraTextareaField, MessageBox } from 'app/components/Core';
import React, { useState } from 'react';
import { fieldValidationMessages } from 'app/constants';

interface PlaylistRoomDescriptionProps {
  playlistTitle: string;
  description: string;
  onUpdate: (description: string) => Promise<void>;
  maxLength: number;
}

export const PlaylistRoomDescription = ({
  playlistTitle,
  description: descriptionProp,
  onUpdate,
  maxLength,
}: PlaylistRoomDescriptionProps) => {
  const dialog = useDisclosure();
  const [description, setDescription] = useState(descriptionProp);
  const [loading, setLoading] = useState(false);

  const hasChanges = description !== descriptionProp;
  const maxLengthReached = maxLength ? description.length > maxLength : false;

  const onSubmit = async () => {
    if (!hasChanges) {
      return;
    }
    try {
      setLoading(true);
      await onUpdate(description);
      dialog.onClose();
    } catch (e) {
      // proceed
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Text
        fontSize="xs"
        my={6}
        data-testid="edit-description"
        onClick={dialog.onOpen}
        maxWidth="600px"
        noOfLines={2}
        cursor="pointer"
        wordBreak="break-word"
        _hover={{
          textDecoration: 'underline',
        }}
      >
        {descriptionProp || 'Add Description'}
      </Text>
      <Modal
        isOpen={dialog.isOpen}
        onClose={dialog.onClose}
        onCloseComplete={() => setDescription(descriptionProp)}
        data-testid="description-dialog"
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader data-testid="dialog-title" title={playlistTitle}>
            {playlistTitle}
          </ModalHeader>
          <ModalBody data-testid="dialog-content">
            <ChakraTextareaField
              label="Description"
              value={description}
              formControlStyleProps={{
                h: '145px',
              }}
              h="145px"
              onChange={(e) => setDescription(e.target.value)}
              resize="none"
              isInvalid={maxLengthReached}
              errorMessage={fieldValidationMessages.MAX_CHAR(maxLength)}
            />
            <MessageBox
              boxProps={{
                mt: 3,
                w: 'full',
                'data-testid': 'info-message',
              }}
            >
              Any description you add will be visible to the person viewing the
              share link.
            </MessageBox>
          </ModalBody>
          <ModalFooter data-testid="dialog-actions">
            <Button
              variant="secondary"
              data-testid="cancel-button"
              onClick={dialog.onClose}
            >
              Cancel
            </Button>
            <Button
              isDisabled={loading || !hasChanges || maxLengthReached}
              data-testid="save-button"
              onClick={onSubmit}
              ml={2}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
